import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import { useAuth } from '../../contexts/AuthContext';

const AccountDropdown = ({ isAuthenticated = false, user = null, isMobile = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { userProfile, isAdmin, isOwner, isRenter, signOut } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event?.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleLogout = async () => {
    setIsOpen(false);
    await signOut();
    navigate('/home-page');
  };

  const handleLogin = () => {
    setIsOpen(false);
  };

  // Determine dashboard path based on user role
  const getDashboardPath = () => {
    if (isAdmin) {
      return '/admin-platform-management';
    }
    if (isOwner) {
      return '/owner-dashboard';
    }
    return '/renter-dashboard';
  };

  if (isMobile) {
    if (!isAuthenticated) {
      return (
        <div className="space-y-2">
          <Link
            to="/login"
            onClick={handleLogin}
            className="w-full flex items-center justify-center space-x-2 px-6 py-4 bg-primary text-primary-foreground rounded-md font-body font-medium transition-smooth hover:opacity-90"
          >
            <Icon name="LogIn" size={20} strokeWidth={2} />
            <span className="text-lg">Sign In</span>
          </Link>
          <Link
            to="/signup"
            onClick={handleLogin}
            className="w-full flex items-center justify-center space-x-2 px-6 py-4 bg-secondary text-secondary-foreground rounded-md font-body font-medium transition-smooth hover:opacity-90"
          >
            <Icon name="UserPlus" size={20} strokeWidth={2} />
            <span className="text-lg">Sign Up</span>
          </Link>
        </div>
      );
    }

    return (
      <div className="space-y-2">
        <div className="flex items-center space-x-3 px-6 py-4 bg-muted rounded-md">
          <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
            <Icon name="User" size={20} color="white" strokeWidth={2} />
          </div>
          <div className="flex-1">
            <p className="font-body font-medium text-foreground">{user?.name || 'User Name'}</p>
            <p className="font-caption text-sm text-muted-foreground">{user?.email || 'user@example.com'}</p>
          </div>
        </div>
        
        <Link
          to={getDashboardPath()}
          className="flex items-center space-x-3 px-6 py-4 text-foreground hover:bg-muted rounded-md transition-smooth"
        >
          <Icon name="LayoutDashboard" size={20} strokeWidth={2} />
          <span className="font-body font-medium text-lg">Dashboard</span>
        </Link>
        
        <Link
          to="/messaging-center"
          className="flex items-center space-x-3 px-6 py-4 text-foreground hover:bg-muted rounded-md transition-smooth"
        >
          <Icon name="MessageSquare" size={20} strokeWidth={2} />
          <span className="font-body font-medium text-lg">Messages</span>
        </Link>
        
        <Link
          to="/reviews-management"
          className="flex items-center space-x-3 px-6 py-4 text-foreground hover:bg-muted rounded-md transition-smooth"
        >
          <Icon name="Star" size={20} strokeWidth={2} />
          <span className="font-body font-medium text-lg">Reviews</span>
        </Link>
        
        <Link
          to="/profile"
          className="flex items-center space-x-3 px-6 py-4 text-foreground hover:bg-muted rounded-md transition-smooth"
        >
          <Icon name="User" size={20} strokeWidth={2} />
          <span className="font-body font-medium text-lg">Profile</span>
        </Link>
        
        <button
          onClick={handleLogout}
          className="w-full flex items-center space-x-3 px-6 py-4 text-error hover:bg-muted rounded-md transition-smooth"
        >
          <Icon name="LogOut" size={20} strokeWidth={2} />
          <span className="font-body font-medium text-lg">Sign Out</span>
        </button>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="flex items-center space-x-3">
        <Link
          to="/login"
          onClick={handleLogin}
          className="px-6 py-3 text-foreground font-body font-medium hover:bg-muted rounded-md transition-smooth"
        >
          Sign In
        </Link>
        <Link
          to="/signup"
          onClick={handleLogin}
          className="px-6 py-3 bg-primary text-primary-foreground font-body font-medium rounded-md transition-smooth hover:opacity-90"
        >
          Sign Up
        </Link>
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-4 py-2 rounded-md hover:bg-muted transition-smooth focus:outline-none focus-ring"
        aria-label="Account menu"
        aria-expanded={isOpen}
      >
        <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
          <Icon name="User" size={20} color="white" strokeWidth={2} />
        </div>
        <Icon name="ChevronDown" size={16} strokeWidth={2} className={`transition-smooth ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-popover rounded-lg shadow-elevation-4 overflow-hidden">
          <div className="px-4 py-3 border-b border-border">
            <p className="font-body font-medium text-foreground">{user?.name || 'User Name'}</p>
            <p className="font-caption text-sm text-muted-foreground">{user?.email || 'user@example.com'}</p>
          </div>
          
          <div className="py-2">
            <Link
              to={getDashboardPath()}
              onClick={() => setIsOpen(false)}
              className="flex items-center space-x-3 px-4 py-3 text-foreground hover:bg-muted transition-smooth"
            >
              <Icon name="LayoutDashboard" size={18} strokeWidth={2} />
              <span className="font-body">Dashboard</span>
            </Link>
          </div>
          
          <div className="border-t border-border py-2">
            <button
              onClick={handleLogout}
              className="w-full flex items-center space-x-3 px-4 py-3 text-error hover:bg-muted transition-smooth"
            >
              <Icon name="LogOut" size={18} strokeWidth={2} />
              <span className="font-body">Sign Out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountDropdown;